/**
 * Diagnostic hook — Inea's strategic diagnosis (Module 1 output).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/api';

export interface ActionStep {
  timeframe: string;
  title: string;
  detail: string;
}

export interface PlatformTone {
  platform: string;
  tone: string;
  advice: string;
}

export interface Diagnostic {
  headline: string;
  recommended_positioning: string;
  positioning_rationale: string;
  strengths: string[];
  blind_spots: string[];
  platform_tones: PlatformTone[];
  action_plan: ActionStep[];
  inea_note: string;
}

export type DiagnosticStatus = 'ready' | 'pending' | 'failed' | 'not_started';

export interface DiagnosticResponse {
  diagnostic: Diagnostic | null;
  status: DiagnosticStatus;
  error: { reason: string; at: string } | null;
}

export function useDiagnostic() {
  const qc = useQueryClient();

  const query = useQuery<DiagnosticResponse>({
    queryKey: ['diagnostic'],
    queryFn: () => apiRequest<DiagnosticResponse>('/onboarding/diagnostic'),
    // Poll only while diagnostic is genuinely pending. Stop on ready, failed,
    // or not_started — none of those need polling.
    refetchInterval: (q) => (q.state.data?.status === 'pending' ? 3000 : false),
  });

  const regenerate = useMutation({
    mutationFn: () =>
      apiRequest<{ diagnostic: Diagnostic }>('/onboarding/diagnostic/regenerate', {
        method: 'POST',
      }),
    onSuccess: (data) =>
      qc.setQueryData<DiagnosticResponse>(['diagnostic'], {
        diagnostic: data.diagnostic,
        status: 'ready',
        error: null,
      }),
  });

  return {
    diagnostic: query.data?.diagnostic ?? null,
    status: query.data?.status ?? 'pending',
    error: query.data?.error ?? null,
    isLoading: query.isLoading,
    regenerate,
  };
}
